import { formatDateDisplay, normalizeDateForStorage, parseFlexibleDate } from './date';

export const SHIFT_LIBRARY = {
  day_5_7: {
    code: 'day_5_7',
    label: '5:00 a. m. a 7:00 p. m.',
    shortLabel: '5 a 7',
    category: 'shift',
    startTime: '05:00',
    endTime: '19:00',
    color: '#2563eb',
    bgClass: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  day_6_8: {
    code: 'day_6_8',
    label: '6:00 a. m. a 8:00 p. m.',
    shortLabel: '6 a 8',
    category: 'shift',
    startTime: '06:00',
    endTime: '20:00',
    color: '#16a34a',
    bgClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  },
  day_7_9: {
    code: 'day_7_9',
    label: '7:00 a. m. a 9:00 p. m.',
    shortLabel: '7 a 9',
    category: 'shift',
    startTime: '07:00',
    endTime: '21:00',
    color: '#f97316',
    bgClass: 'bg-orange-100 text-orange-800 border-orange-200',
  },
  night_6_6: {
    code: 'night_6_6',
    label: 'Nocturno 6:00 p. m. a 6:00 a. m.',
    shortLabel: 'Nocturno',
    category: 'shift',
    startTime: '18:00',
    endTime: '06:00',
    color: '#5b21b6',
    bgClass: 'bg-violet-100 text-violet-900 border-violet-200',
  },
  free: {
    code: 'free',
    label: 'Libre',
    shortLabel: 'Libre',
    category: 'state',
    startTime: '',
    endTime: '',
    color: '#6b7280',
    bgClass: 'bg-slate-100 text-slate-700 border-slate-200',
  },
  sunday: {
    code: 'sunday',
    label: 'Domingo',
    shortLabel: 'Domingo',
    category: 'state',
    startTime: '',
    endTime: '',
    color: '#f87171',
    bgClass: 'bg-rose-100 text-rose-800 border-rose-200',
  },
  compensatory_rest: {
    code: 'compensatory_rest',
    label: 'Descanso compensatorio',
    shortLabel: 'Descanso',
    category: 'state',
    startTime: '',
    endTime: '',
    color: '#eab308',
    bgClass: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  unassigned: {
    code: 'unassigned',
    label: 'Día no asignado',
    shortLabel: 'Sin asignar',
    category: 'state',
    startTime: '',
    endTime: '',
    color: '#94a3b8',
    bgClass: 'bg-slate-50 text-slate-500 border-slate-200',
  },
  vacation: {
    code: 'vacation',
    label: 'Vacaciones',
    shortLabel: 'Vacaciones',
    category: 'state',
    startTime: '',
    endTime: '',
    color: '#0f766e',
    bgClass: 'bg-teal-100 text-teal-800 border-teal-200',
  },
  medical_leave: {
    code: 'medical_leave',
    label: 'Incapacidad',
    shortLabel: 'Incapacidad',
    category: 'state',
    startTime: '',
    endTime: '',
    color: '#ec4899',
    bgClass: 'bg-pink-100 text-pink-800 border-pink-200',
  },
  permission: {
    code: 'permission',
    label: 'Permiso',
    shortLabel: 'Permiso',
    category: 'state',
    startTime: '',
    endTime: '',
    color: '#0891b2',
    bgClass: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  },
};

export const SHIFT_OPTIONS = Object.values(SHIFT_LIBRARY);
const SHIFT_CODES = SHIFT_OPTIONS.map((option) => option.code);

export const DEFAULT_SCHEDULE_SETTINGS = {
  canNightShift: true,
  canNineToNine: true,
  canWorkSunday: true,
  dayOnly: false,
  allowedShiftCodes: SHIFT_CODES,
  preferredDayOff: '',
  notes: '',
};

export const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export const buildAssignmentKey = (userId, date) => `${userId}__${date}`;

export const getAssignmentByKey = (assignments = []) =>
  assignments.reduce((acc, assignment) => {
    acc[buildAssignmentKey(assignment.userId, assignment.date)] = assignment;
    return acc;
  }, {});

export const buildShiftLibrary = (shiftOptions = SHIFT_OPTIONS) =>
  shiftOptions.reduce((acc, option) => {
    acc[option.code] = option;
    return acc;
  }, {});

export const normalizeShiftOptions = (value = []) => {
  if (!Array.isArray(value) || !value.length) {
    return SHIFT_OPTIONS.map((option) => ({ ...option, enabled: true }));
  }

  const base = buildShiftLibrary(SHIFT_OPTIONS);
  const incomingByCode = value.reduce((acc, option) => {
    if (option?.code) acc[option.code] = option;
    return acc;
  }, {});

  const mergedBase = SHIFT_OPTIONS.map((defaultOption) => {
    const option = incomingByCode[defaultOption.code] || {};
    return {
      ...defaultOption,
      ...option,
      code: option?.code || defaultOption.code || '',
      label: option?.label || defaultOption.label || '',
      shortLabel: option?.shortLabel || defaultOption.shortLabel || '',
      category: option?.category || defaultOption.category || 'state',
      startTime: option?.startTime ?? defaultOption.startTime ?? '',
      endTime: option?.endTime ?? defaultOption.endTime ?? '',
      color: option?.color || defaultOption.color || '',
      bgClass: option?.bgClass || defaultOption.bgClass || '',
      enabled: option?.enabled !== false,
    };
  });

  const customOptions = value
    .filter((option) => option?.code && !base[option.code])
    .map((option) => {
      const defaultOption = {};
      return {
        ...defaultOption,
        ...option,
        code: option?.code || defaultOption.code || '',
        label: option?.label || defaultOption.label || '',
        shortLabel: option?.shortLabel || defaultOption.shortLabel || '',
        category: option?.category || defaultOption.category || 'state',
        startTime: option?.startTime ?? defaultOption.startTime ?? '',
        endTime: option?.endTime ?? defaultOption.endTime ?? '',
        color: option?.color || defaultOption.color || '',
        bgClass: option?.bgClass || defaultOption.bgClass || '',
        enabled: option?.enabled !== false,
      };
    })
    .filter((option) => option.code);

  return [...mergedBase, ...customOptions];
};

export const getEntryDefinition = (code, library = SHIFT_LIBRARY) => library[code] || library.unassigned || SHIFT_LIBRARY.unassigned;

export const formatScheduleTimeRange = (startTime, endTime) =>
  startTime && endTime ? `${startTime} - ${endTime}` : '';

const SCHEDULE_TEXT_ES = {
  free: 'Libre',
  off: 'Libre',
  sunday: 'Domingo',
  unassigned: 'Sin asignar',
  vacation: 'Vacaciones',
  medical_leave: 'Incapacidad',
  'medical leave': 'Incapacidad',
  permission: 'Permiso',
  day: 'Turno diurno',
  night: 'Turno nocturno',
};

export const formatScheduleAssignment = (assignment = {}) => {
  const timeRange = formatScheduleTimeRange(assignment.startTime, assignment.endTime);
  if (timeRange) return timeRange;

  const definition = SHIFT_LIBRARY[assignment.code];
  if (definition?.label) return definition.label;

  const rawValue = String(assignment.label || assignment.code || '').trim();
  const translated = SCHEDULE_TEXT_ES[rawValue.toLowerCase().replace(/-/g, '_')];
  return translated || rawValue || 'Sin horario asignado';
};

export const addDays = (dateValue, days) => {
  const date = parseFlexibleDate(dateValue) || new Date();
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return normalizeDateForStorage(copy);
};

export const getFortnightEndDate = (startDate) => addDays(startDate, 14);

export const buildFortnightDays = (startDate, endDate) => {
  const start = parseFlexibleDate(startDate);
  const end = parseFlexibleDate(endDate);
  if (!start || !end) return [];

  const days = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const normalized = normalizeDateForStorage(cursor);
    days.push({
      date: normalized,
      label: formatDateDisplay(normalized),
      dayName: DAY_NAMES[cursor.getDay()],
      isSunday: cursor.getDay() === 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
};

export const normalizeScheduleSettings = (value = {}, allowedShiftCodes = DEFAULT_SCHEDULE_SETTINGS.allowedShiftCodes) => ({
  ...DEFAULT_SCHEDULE_SETTINGS,
  ...value,
  allowedShiftCodes: (() => {
    if (Array.isArray(value?.allowedShiftCodes) && value.allowedShiftCodes.length) {
      const filtered = value.allowedShiftCodes.filter((code) => allowedShiftCodes.includes(code));
      return filtered.length ? filtered : [...allowedShiftCodes];
    }
    return [...allowedShiftCodes];
  })(),
});

export const isOffCode = (code) =>
  ['free', 'sunday', 'compensatory_rest', 'unassigned', 'vacation', 'medical_leave', 'permission'].includes(code);

export const summarizeOwnAssignments = (assignments = [], days = []) => {
  const daySet = new Set(days.map((day) => day.date));
  const filtered = assignments.filter((item) => daySet.has(item.date));
  const today = normalizeDateForStorage(new Date());
  const todayAssignment = filtered.find((item) => item.date === today) || null;
  const freeDays = filtered.filter((item) => item.code === 'free' || item.code === 'compensatory_rest');
  const sundayAssignments = filtered.filter((item) => {
    const date = parseFlexibleDate(item.date);
    return item.code === 'sunday' || (date?.getDay?.() === 0 && !isOffCode(item.code));
  });
  const nightAssignments = filtered.filter((item) => item.code === 'night_6_6');

  return {
    todayAssignment,
    freeDays,
    sundayAssignments,
    nightAssignments,
    filtered,
  };
};
